/**
 * Shared metadata for the `recovery_events.recovery_kind` enum.
 *
 * Centralizes the list of allowed values plus their human-readable labels
 * and Tag colors so the list, show, and filter components don't drift from
 * the server's canonical schema (`apps/server/src/db/schema.ts`). When a new
 * `recovery_kind` value is added on the server, update this file once and
 * every admin component picks it up.
 */
import type { RecoveryKind } from '@/types';

import { RECOVERY_KINDS } from '@/types';

export { RECOVERY_KINDS };
export type { RecoveryKind };

/**
 * AntD `Tag` color for each recovery kind.
 *
 * Severity convention:
 *   - blue / green / gold / default — benign, expected recovery branches
 *   - red                           — outright failure / requires ops action
 *   - volcano                       — projection-side failure (recoverable next boot)
 *   - yellow                        — transient skip, will retry
 *   - default (grey)                — benign no-op
 */
export const RECOVERY_KIND_COLORS: Record<RecoveryKind, string> = {
  generating: 'blue',
  review_silent_replay: 'green',
  review_reemit: 'gold',
  review_no_interrupt_id: 'default',
  review_projection_failed: 'volcano',
  requires_admin_reconciliation: 'red',
  review_recovery_skipped: 'yellow',
  review_fail_terminal: 'red',
  review_no_op: 'default',
};

/** Human-readable label for each recovery kind, shown in tags and the filter dropdown. */
export const RECOVERY_KIND_LABELS: Record<RecoveryKind, string> = {
  generating: 'Generating',
  review_silent_replay: 'Silent replay',
  review_reemit: 'Re-emit',
  review_no_interrupt_id: 'No interrupt id',
  review_projection_failed: 'Projection failed',
  requires_admin_reconciliation: 'Requires admin reconciliation',
  review_recovery_skipped: 'Review recovery skipped',
  review_fail_terminal: 'Review fail terminal',
  review_no_op: 'Review no-op',
};
